import {
  DeviceType,
  OverRides,
  type PopupBackgroundGradient,
  SSOMarketingPopup,
  SSOMarketingPopupBackendData,
} from "./types";

/** Build CSS `linear-gradient` for popup shell `background` (not `background-color`). */
export function buildPopupLinearGradientBackground(g: PopupBackgroundGradient): string {
  const mid = Math.min(100, Math.max(0, g.midStopPercent));
  return `linear-gradient(${g.angleDeg}deg, ${g.colorFrom} 0%, ${g.colorTo} ${mid}%)`;
}

const gradientCssRe =
  /^linear-gradient\(\s*(\d+)\s*deg\s*,\s*(#[0-9A-Fa-f]{3,8}|rgb\([^)]+\))\s+0%\s*,\s*(#[0-9A-Fa-f]{3,8}|rgb\([^)]+\))\s+(\d+)\s*%\s*\)\s*$/i;

export function parsePopupLinearGradientBackground(
  css: string,
): PopupBackgroundGradient | null {
  const m = css.trim().match(gradientCssRe);
  if (!m || m[1] === undefined || m[2] === undefined || m[3] === undefined || m[4] === undefined) {
    return null;
  }
  return {
    enabled: true,
    colorFrom: m[2],
    colorTo: m[3],
    angleDeg: Number(m[1]),
    midStopPercent: Number(m[4]),
  };
}

function normalizePopupShellBackground(raw: string): string {
  const trimmed = raw.trim();
  const parsedGradient = parsePopupLinearGradientBackground(trimmed);
  if (parsedGradient) {
    return parsedGradient.colorFrom;
  }
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.toLowerCase().includes("linear-gradient")) {
    return "#FFFFFF";
  }
  return trimmed || "#FFFFFF";
}

export const parsePopupFromBackend = (
  popup: SSOMarketingPopupBackendData,
): SSOMarketingPopup => {
  const bg = popup.background_color ?? "";
  const parsedGradient = parsePopupLinearGradientBackground(bg);

  return {
    shouldLoadFontFaces: popup.should_load_font_faces,
    fontType: popup.font_type,
    googleFontFaces:
      popup.google_font_faces?.map((font) => ({
        familyName: font.family_name,
        src: font.src,
        style: font.style,
        weight: font.weight,
        display: font.display,
        unicodeRange: font.unicode_range,
      })) ?? [],
    customFontFaces: popup?.custom_font_faces ?? [],
    logoUrl: popup.logo_url,
    backgroundColor: normalizePopupShellBackground(bg),
    preLoginContentAlignment: popup.pre_login_content_alignment ?? "center",
    popupBackgroundGradient: parsedGradient ?? undefined,
    popTriggerTime: popup.pop_trigger_time,
    borderRadius: popup.border_radius,
    primaryCTA: {
      text: popup.primary_cta.text,
      color: popup.primary_cta.color,
      backgroundColor: popup.primary_cta.background_color,
    },
    // Todo: Genish remove the optional chaining
    title: {
      text: popup.title?.text,
      color: popup.title?.color,
    },
    subTitle: popup.sub_title
      ? {
          text: popup.sub_title?.text,
          color: popup.sub_title?.color,
        }
      : undefined,
    banner: {
      enabled: popup.banner.enabled,
      backgroundColor: popup.banner.background_color,
      textColor: popup.banner.text_color,
      items: popup.banner.items.map((item) => ({
        type: item.type,
        enabled: item.enabled,
        textColor: item.text_color,
        backgroundColor: item.background_color ?? popup.banner.background_color,
        htmlTextString: item.html_text_string,
      })),
    },
    card: {
      enabled: popup.card.enabled,
      backgroundColor: popup.card.background_color,
      backgroundImage: popup.card.background_image,
      textColor: popup.card.text_color,
      borderColor: popup.card.border_color,
      borderWidth: popup.card.border_width,
      borderRadius: popup.card.border_radius,
      items: popup.card.items.map((item) => ({
        backgroundImageFit: item.background_image_fit || "cover",
        isTopIconTextOrImage: item.is_top_icon_text_or_image,
        backgroundColor: item.background_color,
        backgroundImage: item.background_image,
        topIconText: item.top_icon_text,
        topIconTextColor: item.top_icon_text_color,
        topIconImage: item.top_icon_image,

        title: item.title,
        titleColor: item.title_color,

        description: item.description,
        descriptionColor: item.description_color,

        borderColor: item.border_color,
        borderWidth: item.border_width,
        borderRadius: item.border_radius,
      })),
    },
    ...(popup.post_login && {
      postLogin: {
        backgroundColor:
          popup.post_login?.background_color ?? popup.background_color ?? "",
        primaryCTA: {
          text:
            popup.post_login?.primary_cta?.text ??
            popup.primary_cta?.text ??
            "",
          color:
            popup.post_login?.primary_cta?.color ??
            popup.primary_cta?.color ??
            "",
          backgroundColor:
            popup.post_login?.primary_cta?.background_color ??
            popup.primary_cta?.background_color ??
            "",
        },
        title: {
          text: popup.post_login?.title?.text ?? popup.title?.text ?? "",
          color: popup.post_login?.title?.color ?? popup.title?.color ?? "",
        },
        subtitle: {
          text:
            popup.post_login?.sub_title?.text ?? popup.sub_title?.text ?? "",
          color:
            popup.post_login?.sub_title?.color ?? popup.sub_title?.color ?? "",
        },
        discountCardEnabled: popup.post_login?.discount_card_enabled ?? false,
      },
    }),
  };
};

export const isMobilePreviewMode = (overRides: OverRides | undefined) => {
  if (!overRides) return false;
  return overRides?.isPreview && overRides?.device === DeviceType.MOBILE;
};

export const adjustPixelValue = (
  value: string | undefined,
  adjustment: number,
): string => {
  if (!value) return "0px";
  return value.replace(
    /(\d+)px/,
    (match, num) => `${Number(num) + adjustment}px`,
  );
};

export const getTextLength = (htmlContent: string): number => {
  const textContent = htmlContent.replace(/<[^>]*>/g, "");
  return textContent.trim().length;
};

export const getHtmlBannerText = (
  htmlTextString: string,
  overRides: OverRides | undefined,
  type: "title" | "subTitle" | "banner",
): string => {
  if (getTextLength(htmlTextString) === 0 && Boolean(overRides?.isPreview)) {
    return getNullText(type);
  }
  return htmlTextString;
};

const getNullText = (type: "title" | "subTitle" | "banner"): string => {
  switch (type) {
    case "title":
      return "";
    case "subTitle":
      return "";
    case "banner":
      return "• Add banner text •";
  }
};
